import { InstallAppButton } from "@/components/InstallAppButton";
import { useState, useEffect } from "react";
import axios from "axios";
import { MyWorklistCard } from "@/components/MyWorklistCard";
import { FreeshipTodayCard } from "@/components/FreeshipTodayCard";
import { useSidebarHover } from "@/components/SidebarHoverLabel";
import { PinBtn, PinnedGroup } from "@/components/SidebarPins";
import AccountsPanel from "@/components/accounts/AccountsPanel";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  LogOut, LayoutDashboard, CheckSquare, StickyNote, FolderKanban, Calculator, 
  MessageCircle, Users, UserCog, Shield, ChevronLeft, ChevronRight,
  BarChart3, Clock, AlertCircle, CheckCircle2, HelpCircle, Building2, Repeat, FileText,
  Menu, X, ChevronDown, FileSpreadsheet, Pencil, Package, Database, Landmark, Scissors, FileOutput, Tag,
  GitBranch, IndianRupee, Ban, RotateCcw, ClipboardList
} from "lucide-react";

// Import panel components
import TasksPanel from "@/components/TasksPanel";
import NotesPanel from "@/components/NotesPanel";
import ProjectsPanel from "@/components/ProjectsPanel";
import TeamChat from "@/components/TeamChat";
import { ChatUnreadBadge } from "@/components/chat/ChatUnreadBadge";
import PartnerManagementPanel from "@/components/PartnerManagementPanel";
import { Handshake } from "lucide-react";
import UserApproval from "@/components/UserApproval";
import AdminUserManagement from "@/components/AdminUserManagement";
import PartnerChatsPanel from "@/components/PartnerChatsPanel";
import TeamsManagementPanel from "@/components/TeamsManagementPanel";
import RecurringTasksPanel from "@/components/RecurringTasksPanel";
import DocumentsPanel from "@/components/DocumentsPanel";
import UsaProductCodesPanel from "@/components/UsaProductCodesPanel";
import ProductAliasesPanel from "@/components/ProductAliasesPanel";
import ReturnedParcelsPanel from "@/components/ReturnedParcelsPanel";
import MindMapPanel from "@/components/MindMapPanel";
import ReportsPanel from "@/components/ReportsPanel";
import DatabaseAPanel from "@/components/DatabaseAPanel";
import PayrollPanel from "@/components/PayrollPanel";
import ShipmentsPanel from "@/components/ShipmentsPanel";
import PbFormPanel from "@/components/PbFormPanel";
import EdfFormPanel from "@/components/EdfFormPanel";
import NotificationBell from "@/components/NotificationBell";
import { CorrectionAlertBell, CorrectionAlertBanner } from "@/components/CorrectionAlerts";
import { MissingFilesBadge } from "@/components/MissingFilesBadge";
import InvoiceDocPanel from "@/components/InvoiceDocPanel";
import AdvanceTrackingPanel from "@/components/AdvanceTrackingPanel";
import EdfDocPanel from "@/components/EdfDocPanel";
import PbDocPanel from "@/components/PbDocPanel";
import InvoiceStepNotifier from "@/components/InvoiceStepNotifier";
import ProcessingTeamPanel from "@/components/ProcessingTeamPanel";
import OrderRegisterTab from "@/components/OrderRegisterTab";
import ErrorBoundary from "@/components/ErrorBoundary";

const Dashboard = ({ setIsAuthenticated }) => {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePanel, setActivePanel] = useState("dashboard");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [pins, setPins] = useState([]);
  const togglePin = (id, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    const next = pins.includes(id) ? pins.filter(x => x !== id) : [...pins, id];
    setPins(next);
    axios.put(`${API}/users/me/ui-prefs`, { ui_prefs: { sidebar_pins: next } }, { headers: getAuthHeader() }).catch(() => {});
  };
  useEffect(() => {
    axios.get(`${API}/users/me/ui-prefs`, { headers: getAuthHeader() })
      .then(r => {
        const v = r.data?.ui_prefs?.sidebar_collapsed; if (typeof v === "boolean") setSidebarCollapsed(v);
        if (Array.isArray(r.data?.ui_prefs?.sidebar_pins)) setPins(r.data.ui_prefs.sidebar_pins);
      })
      .catch(() => {});
  }, []);
  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    axios.put(`${API}/users/me/ui-prefs`, { ui_prefs: { sidebar_collapsed: next } }, { headers: getAuthHeader() }).catch(() => {});
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const sideHover = useSidebarHover(sidebarCollapsed && !mobileMenuOpen);
  const [documentsExpanded, setDocumentsExpanded] = useState(false);

  // Deep-link support: ?panel=doc_edf_form navigates automatically, and a
  // custom "panelchange" event lets other components request navigation too
  // (e.g., ShipmentDetail → Reprint EDF).
  useEffect(() => {
    const applyFromUrl = () => {
      try {
        const p = new URLSearchParams(window.location.search).get("panel");
        if (p) {
          setActivePanel(p);
          if (p.startsWith("doc_")) setDocumentsExpanded(true);
        }
      } catch { /* ignore */ }
    };
    applyFromUrl();
    window.addEventListener("panelchange", applyFromUrl);
    window.addEventListener("popstate", applyFromUrl);
    return () => {
      window.removeEventListener("panelchange", applyFromUrl);
      window.removeEventListener("popstate", applyFromUrl);
    };
  }, []);

  // Document sub-panels
  const documentSubPanels = [
    { id: "doc_excel_transpose", key: "excel_transpose", label: "Excel Transpose", icon: FileSpreadsheet },
    { id: "doc_pdf_edits", key: "pdf_edits", label: "PDF Edits", icon: Pencil },
    { id: "doc_packing_list", key: "packing_list", label: "Packing List", icon: Package },
    { id: "doc_bulk_data", key: "bulk_data", label: "Bulk Data", icon: Database },
    { id: "doc_manage_companies", key: "manage_companies", label: "Manage Companies", icon: Landmark },
    { id: "doc_pack_team_print", key: "pack_team_print", label: "Pack Team Print", icon: Scissors },
    { id: "doc_pdf_to_word", key: "pdf_to_word", label: "PDF to Word", icon: FileOutput },
    { id: "doc_bulk_label", key: "bulk_label", label: "Bulk Label", icon: Tag },
    { id: "doc_invoice", key: "invoice", label: "Invoice", icon: FileText },
    { id: "doc_edf", key: "edf", label: "EDF Form", icon: FileText },
    { id: "doc_pb_form", key: "pb_form", label: "PB Form", icon: FileText },
    { id: "doc_usa_product_codes", key: "usa_product_codes", label: "USA Product Codes", icon: Package },
    { id: "doc_product_aliases", key: "product_aliases", label: "Product Aliases", icon: Package },
    { id: "doc_returned_parcels", key: "returned_parcels", label: "Returned Parcels", icon: RotateCcw },
    { id: "doc_payroll", key: "payroll", label: "Payroll", icon: IndianRupee },
    { id: "doc_shipments", key: "shipments", label: "Shipments", icon: Package },
    { id: "doc_pb_forms", key: "pb_forms", label: "PB Forms (manual fill)", icon: FileText },
    { id: "doc_edf_form", key: "edf_form", label: "EDF Form No. Pool", icon: FileText },
    { id: "doc_advance_tracking", key: "advance_tracking", label: "Advance Tracking", icon: Package },
  ];

  // Navigation items
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, adminOnly: false },
    { id: "tasks", label: "Tasks", icon: CheckSquare, adminOnly: false },
    { id: "recurring", label: "Recurring Tasks", icon: Repeat, adminOnly: false },
    { id: "notes", label: "Notes", icon: StickyNote, adminOnly: false },
    { id: "projects", label: "Projects", icon: FolderKanban, adminOnly: false },
    { id: "documents", label: "Documents", icon: FileText, adminOnly: false, hasSubPanels: true },
    { id: "mindmap", label: "Mind Map", icon: GitBranch, adminOnly: false },
    { id: "reports", label: "Reports", icon: BarChart3, adminOnly: false },
    { id: "database_a", label: "Database A", icon: Database, adminOnly: false },
    { id: "processing_team", label: "Processing Team", icon: Ban, adminOnly: false },
    // Iter 176 - promoted from a Processing Team sub-panel to a
    // top-level entry (large dataset, primary daily driver).
    { id: "order_register", label: "Order Register", icon: ClipboardList, adminOnly: false },
    { id: "chat", label: "Team Chat", icon: MessageCircle, adminOnly: false },
    { id: "queries", label: "Partner Queries", icon: HelpCircle, adminOnly: false },
    { id: "partner_mgmt", label: "Partner Management", icon: Handshake, adminOnly: false },
    { id: "accounts", label: "Accounts", icon: Calculator, adminOnly: false },
  ];

  const adminNavItems = [
    { id: "teams", label: "Teams Management", icon: Building2, adminOnly: true },
    { id: "users", label: "User Management", icon: UserCog, adminOnly: true },
    { id: "approvals", label: "Pending Approvals", icon: Shield, adminOnly: true },
  ];

  useEffect(() => {
    const label = [...documentSubPanels, ...navItems, ...adminNavItems].find(i => i.id === activePanel)?.label || "Dashboard";
    document.title = `TeamOne | ${label}`;
  }, [activePanel]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, statsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers: getAuthHeader() }),
        axios.get(`${API}/stats`, { headers: getAuthHeader() })
      ]);
      setUser(userRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeader() }).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
  };

  useEffect(() => {  // portaled dialogs / selects opened from a view-only panel get the same hide rules
    const pk = (activePanel || "").startsWith("doc_") ? activePanel.slice(4) : activePanel;
    const vo = !!(user && user.role !== "admin" && pk && (user.panel_rights || {})[pk] === "view");
    document.body.classList.toggle("panel-view-only-active", vo);
    return () => document.body.classList.remove("panel-view-only-active");
  }, [user, activePanel]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Dashboard Overview Component
  const DashboardOverview = () => (
    <div className="space-y-6">
      <MyWorklistCard />
      <FreeshipTodayCard />
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Total Tasks</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Pending</p>
                <p className="text-3xl font-bold text-amber-600 mt-1">{stats?.pending || 0}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">In Progress</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{stats?.in_progress || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Completed</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{stats?.completed || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admin Team Stats */}
      {user?.role === 'admin' && stats?.team_stats && (
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Team Statistics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {Object.entries(stats.team_stats).map(([team, teamStats]) => (
                <div key={team} className="p-4 rounded-xl border border-slate-200 bg-slate-50">
                  <h4 className="font-semibold text-slate-900 mb-2">{team}</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total:</span>
                      <span className="font-medium">{teamStats.total}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-amber-600">Pending:</span>
                      <span className="font-medium">{teamStats.pending}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-600">In Progress:</span>
                      <span className="font-medium">{teamStats.in_progress}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-600">Completed:</span>
                      <span className="font-medium">{teamStats.completed}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button 
          onClick={() => setActivePanel("tasks")}
          className="p-6 bg-white/80 rounded-xl border-0 shadow-lg hover:shadow-xl transition-all text-left group relative"
        >
          {stats?.pending > 0 && (
            <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {stats.pending} pending
            </div>
          )}
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <CheckSquare className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-semibold text-slate-900">Tasks</h3>
          <p className="text-sm text-slate-500 mt-1">{stats?.total || 0} total tasks</p>
        </button>

        <button 
          onClick={() => setActivePanel("notes")}
          className="p-6 bg-white/80 rounded-xl border-0 shadow-lg hover:shadow-xl transition-all text-left group relative"
        >
          {stats?.notes_count > 0 && (
            <div className="absolute top-3 right-3 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {stats.notes_count}
            </div>
          )}
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <StickyNote className="w-6 h-6 text-amber-600" />
          </div>
          <h3 className="font-semibold text-slate-900">Notes</h3>
          <p className="text-sm text-slate-500 mt-1">{stats?.notes_count || 0} notes</p>
        </button>

        <button 
          onClick={() => setActivePanel("projects")}
          className="p-6 bg-white/80 rounded-xl border-0 shadow-lg hover:shadow-xl transition-all text-left group relative"
        >
          {stats?.projects_count > 0 && (
            <div className="absolute top-3 right-3 bg-violet-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {stats.projects_count}
            </div>
          )}
          <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <FolderKanban className="w-6 h-6 text-violet-600" />
          </div>
          <h3 className="font-semibold text-slate-900">Projects</h3>
          <p className="text-sm text-slate-500 mt-1">{stats?.projects_count || 0} projects</p>
        </button>

        <button 
          onClick={() => setActivePanel("chat")}
          className="p-6 bg-white/80 rounded-xl border-0 shadow-lg hover:shadow-xl transition-all text-left group relative"
        >
          {stats?.chat_messages_count > 0 && (
            <div className="absolute top-3 right-3 bg-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {stats.chat_messages_count}
            </div>
          )}
          <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <MessageCircle className="w-6 h-6 text-pink-600" />
          </div>
          <h3 className="font-semibold text-slate-900">Team Chat</h3>
          <p className="text-sm text-slate-500 mt-1">{stats?.chat_messages_count || 0} messages</p>
        </button>
      </div>
    </div>
  );

  // Render active panel
  // Map every renderable panel key to the permission key it requires.
  // Sub-panels of Documents are mapped to their document_sub_panels flag.
  const PANEL_TO_PERMISSION = {
    dashboard: { type: "panel", key: "dashboard" },
    tasks: { type: "panel", key: "tasks" },
    recurring: { type: "panel", key: "recurring" },
    notes: { type: "panel", key: "notes" },
    projects: { type: "panel", key: "projects" },
    mindmap: { type: "panel", key: "mindmap" },
    reports: { type: "panel", key: "reports" },
    database_a: { type: "panel", key: "database_a" },
    processing_team: { type: "panel", key: "processing_team" },
    order_register: { type: "panel", key: "order_register" },
    chat: { type: "panel", key: "chat" },
    queries: { type: "panel", key: "queries" },
    doc_excel_transpose: { type: "doc", key: "excel_transpose" },
    doc_pdf_edits: { type: "doc", key: "pdf_edits" },
    doc_packing_list: { type: "doc", key: "packing_list" },
    doc_bulk_data: { type: "doc", key: "bulk_data" },
    doc_manage_companies: { type: "doc", key: "manage_companies" },
    doc_pack_team_print: { type: "doc", key: "pack_team_print" },
    doc_pdf_to_word: { type: "doc", key: "pdf_to_word" },
    doc_bulk_label: { type: "doc", key: "bulk_label" },
    doc_invoice: { type: "doc", key: "invoice" },
    doc_edf: { type: "doc", key: "edf" },
    doc_pb_form: { type: "doc", key: "pb_form" },
    doc_usa_product_codes: { type: "doc", key: "usa_product_codes" },
    doc_product_aliases: { type: "doc", key: "product_aliases" },
    doc_returned_parcels: { type: "doc", key: "returned_parcels" },
    doc_payroll: { type: "doc", key: "payroll" },
    doc_shipments: { type: "doc", key: "shipments" },
    doc_pb_forms: { type: "doc", key: "pb_forms" },
    doc_edf_form: { type: "doc", key: "edf_form" },
    doc_advance_tracking: { type: "doc", key: "advance_tracking" },
    // Admin-only routes — role check below covers them
    teams: { type: "admin" },
    users: { type: "admin" },
    approvals: { type: "admin" },
  };

  const PROCESSING_SUB_PANEL_KEYS = [
    "banned_products",
    "currency_converter",
    "company_print",
    "upload_panel",
    "print_panel",
  ];

  const anyDocSubPanelGranted = () => {
    const sub = user?.document_sub_panels || {};
    return Object.values(sub).some((v) => v === true);
  };

  const anyProcessingSubPanelGranted = () => {
    const sub = user?.processing_team_sub_panels || {};
    return PROCESSING_SUB_PANEL_KEYS.some((k) => sub[k] === true);
  };

  const hasPanelAccess = (panelKey) => {
    if (user?.role === "admin") return true;
    const spec = PANEL_TO_PERMISSION[panelKey];
    if (!spec) return true; // default view (dashboard)
    if (spec.type === "admin") return false;
    if (spec.type === "panel") {
      // Special case: Documents & Processing Team parents auto-grant if ANY
      // of their child sub-panels is granted (independent sub-panel access).
      if (spec.key === "documents") {
        return (user?.panel_access || {})["documents"] === true || anyDocSubPanelGranted();
      }
      if (spec.key === "processing_team") {
        return (user?.panel_access || {})["processing_team"] === true || anyProcessingSubPanelGranted();
      }
      // Order Register is its own top-level panel; admins allot it through
      // the Processing Team → "Order Register" checkbox (with its scoping),
      // so that flag is the grant — not the legacy Processing Team sub-tab.
      if (spec.key === "order_register") {
        return (user?.processing_team_sub_panels || {}).order_register === true;
      }
      return (user?.panel_access || {})[spec.key] === true;
    }
    if (spec.type === "doc") {
      // Sub-panel access is independent — no parent panel_access required.
      return (user?.document_sub_panels || {})[spec.key] === true;
    }
    return false;
  };

  const AccessDenied = () => (
    <div className="flex items-center justify-center min-h-[400px]" data-testid="panel-access-denied">
      <div className="max-w-md text-center p-8 rounded-lg border-2 border-red-200 bg-red-50/50">
        <div className="w-14 h-14 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-3">
          <Shield className="w-7 h-7" />
        </div>
        <h3 className="text-lg font-semibold text-red-800 mb-1">Access Denied</h3>
        <p className="text-sm text-red-600">You do not have permission to view this panel. Contact your administrator.</p>
      </div>
    </div>
  );

  const _pk = PANEL_TO_PERMISSION[activePanel]?.key;
  const viewOnlyPanel = !!(user && user.role !== "admin" && _pk && (user.panel_rights || {})[_pk] === "view");
  const renderPanel = () => {
    // Default-deny guard — enforces access even if a user knows the URL / testid.
    if (!hasPanelAccess(activePanel)) return <AccessDenied />;

    switch (activePanel) {
      case "dashboard":
        return <DashboardOverview />;
      case "tasks":
        return <TasksPanel user={user} />;
      case "recurring":
        return <RecurringTasksPanel user={user} />;
      case "notes":
        return <NotesPanel user={user} />;
      case "projects":
        return <ProjectsPanel user={user} />;
      case "doc_excel_transpose":
        return <DocumentsPanel user={user} subPanel="excel_transpose" />;
      case "doc_pdf_edits":
        return <DocumentsPanel user={user} subPanel="pdf_edits" />;
      case "doc_packing_list":
        return <DocumentsPanel user={user} subPanel="packing_list" />;
      case "doc_bulk_data":
        return <DocumentsPanel user={user} subPanel="bulk_data" />;
      case "doc_manage_companies":
        return <DocumentsPanel user={user} subPanel="manage_companies" />;
      case "doc_pack_team_print":
        return <DocumentsPanel user={user} subPanel="pack_team_print" />;
      case "doc_pdf_to_word":
        return <DocumentsPanel user={user} subPanel="pdf_to_word" />;
      case "doc_bulk_label":
        return <DocumentsPanel user={user} subPanel="bulk_label" />;
      case "doc_usa_product_codes":
        return <UsaProductCodesPanel user={user} />;
      case "doc_product_aliases":
        return <ProductAliasesPanel user={user} />;
      case "doc_returned_parcels":
        return <ReturnedParcelsPanel user={user} />;
      case "doc_payroll":
        return <PayrollPanel user={user} />;
      case "doc_invoice":
        return <InvoiceDocPanel user={user} />;
      case "doc_advance_tracking":
        return <AdvanceTrackingPanel />;
      case "doc_edf":
        return <EdfDocPanel user={user} />;
      case "doc_pb_form":
        return <PbDocPanel user={user} />;
      case "doc_shipments":
        return <ShipmentsPanel user={user} />;
      case "doc_pb_forms":
        return <PbFormPanel user={user} />;
      case "doc_edf_form":
        return <EdfFormPanel user={user} />;
      case "mindmap":
        return <MindMapPanel user={user} />;
      case "reports":
        return <ReportsPanel user={user} />;
      case "accounts":
        return <AccountsPanel user={user} />;
      case "database_a":
        return <DatabaseAPanel user={user} />;
      case "processing_team":
        return <ProcessingTeamPanel user={user} />;
      case "order_register":
        return (
          <ErrorBoundary name="order_register">
            <OrderRegisterTab user={user} />
          </ErrorBoundary>
        );
      case "chat":
        return <TeamChat user={user} />;
      case "partner_mgmt":
        return <PartnerManagementPanel />;
      case "queries":
        return <PartnerChatsPanel />;
      case "teams":
        return <TeamsManagementPanel />;
      case "users":
        return <AdminUserManagement />;
      case "approvals":
        return <UserApproval />;
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Hidden on mobile by default, shown when mobileMenuOpen */}
      <aside className={`
        ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'} 
        w-64
        bg-white border-r border-slate-200 
        flex flex-col transition-all duration-300 
        fixed h-screen z-40
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                <Users className="w-5 h-5 text-white" />
              </div>
              {(!sidebarCollapsed || mobileMenuOpen) && (
                <div>
                  <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>TeamOne</h1>
                  <p className="text-xs text-slate-500">{user?.role === 'admin' ? 'Admin Panel' : `${user?.team}`}</p>
                </div>
              )}
            </div>
            {/* Close button for mobile */}
            <button 
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Main Nav - filtered by user's panel_access */}
          <div className="space-y-1">
            <PinnedGroup pins={pins} show={!sidebarCollapsed || mobileMenuOpen} activePanel={activePanel} bind={sideHover.bind}
              pool={[
                ...navItems.filter(i => !i.hasSubPanels && (user?.role === 'admin' || (i.id === 'order_register' ? hasPanelAccess('order_register') : (user?.panel_access || {})[i.id] === true || (i.id === 'processing_team' && anyProcessingSubPanelGranted())))),
                ...documentSubPanels.filter(sp => user?.role === 'admin' || (user?.document_sub_panels || {})[sp.key] === true),
                ...(user?.role === 'admin' ? adminNavItems : []),
              ]}
              onOpen={(id) => { setActivePanel(id); setMobileMenuOpen(false); }} onUnpin={togglePin}
              onReorder={(next) => { setPins(next); axios.put(`${API}/users/me/ui-prefs`, { ui_prefs: { sidebar_pins: next } }, { headers: getAuthHeader() }).catch(() => {}); }} />
            {(!sidebarCollapsed || mobileMenuOpen) && <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-3">Main</p>}
            {navItems
              .filter(item => {
                // Super admin always sees all panels
                if (user?.role === 'admin') return true;
                // DEFAULT-DENY: only show panels explicitly granted (=== true).
                // A missing key OR a false value = no access.
                const panelAccess = user?.panel_access || {};
                // Order Register is granted by its Processing Team checkbox
                if (item.id === 'order_register') return hasPanelAccess('order_register');
                if (panelAccess[item.id] === true) return true;
                // Auto-show parent panel if the user has ANY granted sub-panel
                // (independent sub-panel assignment).
                if (item.id === 'documents' && anyDocSubPanelGranted()) return true;
                if (item.id === 'processing_team' && anyProcessingSubPanelGranted()) return true;
                return false;
              })
              .map(item => {
                // Documents has sub-panels
                if (item.hasSubPanels) {
                  const isDocActive = activePanel.startsWith('doc_') || documentsExpanded;
                  const visibleSubPanels = documentSubPanels.filter(sp => {
                    if (user?.role === 'admin') return true;
                    // DEFAULT-DENY on sub-panels too
                    const subAccess = user?.document_sub_panels || {};
                    return subAccess[sp.key] === true;
                  });
                  
                  return (
                    <div key={item.id}>
                      <button
                        onClick={() => {
                          setDocumentsExpanded(!documentsExpanded);
                          // If clicking documents for first time, select first visible sub-panel
                          if (!documentsExpanded && !activePanel.startsWith('doc_') && visibleSubPanels.length > 0) {
                            setActivePanel(visibleSubPanels[0].id);
                            setMobileMenuOpen(false);
                          }
                        }}
                        data-testid={`nav-${item.id}`}
                        {...sideHover.bind(item.label, visibleSubPanels.map(sp => ({ ...sp, active: activePanel === sp.id,
                          onPick: () => { setActivePanel(sp.id); setDocumentsExpanded(true); } })))}
                        className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                          isDocActive 
                            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' 
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        {(!sidebarCollapsed || mobileMenuOpen) && (
                          <>
                            <span className="font-medium flex-1 text-left">{item.label}</span>
                            <ChevronDown className={`w-4 h-4 transition-transform ${documentsExpanded ? 'rotate-180' : ''}`} />
                          </>
                        )}
                      </button>
                      {/* Sub-panels */}
                      {documentsExpanded && (!sidebarCollapsed || mobileMenuOpen) && (
                        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-slate-200 pl-2">
                          {visibleSubPanels.map(sp => (
                            <button
                              key={sp.id}
                              onClick={() => {
                                setActivePanel(sp.id);
                                setMobileMenuOpen(false);
                              }}
                              data-testid={`nav-${sp.id}`}
                              className={`group w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm ${
                                activePanel === sp.id 
                                  ? 'bg-blue-50 text-blue-700 font-semibold' 
                                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                              }`}
                            >
                              <sp.icon className="w-4 h-4 flex-shrink-0" />
                              <span className="flex-1 text-left">{sp.label}</span>
                              <PinBtn on={pins.includes(sp.id)} onClick={(e) => togglePin(sp.id, e)} testid={`pin-${sp.id}`} />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActivePanel(item.id);
                      setMobileMenuOpen(false);
                    }}
                    data-testid={`nav-${item.id}`}
                    {...sideHover.bind(item.label)}
                    className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      activePanel === item.id 
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' 
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {(!sidebarCollapsed || mobileMenuOpen) && <span className="font-medium flex-1 text-left">{item.label}</span>}
                    {item.id === "chat" && <ChatUnreadBadge active={activePanel === "chat"} />}
                    {(!sidebarCollapsed || mobileMenuOpen) && <PinBtn on={pins.includes(item.id)} onClick={(e) => togglePin(item.id, e)} testid={`pin-${item.id}`} />}
                  </button>
                );
              })}
          </div>

          {/* Admin Nav */}
          {user?.role === 'admin' && (
            <div className="space-y-1 pt-4 mt-4 border-t border-slate-200">
              {(!sidebarCollapsed || mobileMenuOpen) && <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-3">Admin</p>}
              {adminNavItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActivePanel(item.id);
                    setMobileMenuOpen(false); // Close menu on mobile after selection
                  }}
                  data-testid={`nav-${item.id}`}
                  {...sideHover.bind(item.label)}
                  className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    activePanel === item.id 
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {(!sidebarCollapsed || mobileMenuOpen) && <span className="font-medium flex-1 text-left">{item.label}</span>}
                  {(!sidebarCollapsed || mobileMenuOpen) && <PinBtn on={pins.includes(item.id)} onClick={(e) => togglePin(item.id, e)} testid={`pin-${item.id}`} />}
                </button>
              ))}
            </div>
          )}
        </nav>
        <div className="p-4 border-t border-slate-200 lg:hidden"><InstallAppButton /></div>

        {sideHover.layer}
        {/* Collapse Toggle - Hidden on mobile */}
        <div className="p-4 border-t border-slate-200 hidden lg:block">
          <button
            onClick={toggleSidebar} data-testid="sidebar-collapse-toggle"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-all"
          >
            {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            {!sidebarCollapsed && <span className="text-sm">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'} ml-0 transition-all duration-300 w-full`}>
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-10">
          <div className="px-4 lg:px-6 py-4 flex justify-between items-center">
            {/* Mobile Menu Button + Title */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
                data-testid="mobile-menu-button"
              >
                <Menu className="w-6 h-6 text-slate-600" />
              </button>
              <div>
                <h2 className="text-lg lg:text-xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {documentSubPanels.find(i => i.id === activePanel)?.label ||
                   navItems.find(i => i.id === activePanel)?.label || 
                   adminNavItems.find(i => i.id === activePanel)?.label || 
                   'Dashboard'}
                </h2>
                <p className="text-xs lg:text-sm text-slate-500 hidden sm:block">
                  {user?.role === 'admin' ? 'Admin access - All teams' : `${user?.team} Team`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 lg:gap-4">
              <InvoiceStepNotifier />
              <MissingFilesBadge user={user} onOpen={(p) => { setActivePanel(p); setDocumentsExpanded(true); }} />
              <CorrectionAlertBell />
              <NotificationBell />
              <div className="flex items-center gap-2 lg:gap-3">
                <Avatar className="w-8 h-8 lg:w-10 lg:h-10">
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-semibold text-xs lg:text-sm">
                    {user?.name?.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="text-right hidden md:block">
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="p-4 lg:p-6">
          <CorrectionAlertBanner />
          {(() => {
            if (!viewOnlyPanel) return renderPanel();
            return (
              <div className="panel-view-only" data-testid="panel-view-only">
                <div className="mb-3 flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="panel-view-only-banner">
                  <b>View only</b> — you can open, search and download here. Ask an admin for edit rights to make changes.
                </div>
                {renderPanel()}
              </div>);
          })()}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
